<script setup>
import { computed } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

const layout = computed(() => props.link?.options?.layout || 'classic')
const coverImageUrl = computed(() =>
  buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file)
)
const contact = computed(() => {
  const raw = props.link?.contact_details
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) {
      // If contact_details is a plain text (not JSON), fall back to empty object
      return {}
    }
  }
  return raw
})

function onClick(){ emit('click', props.link) }
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }

// Open phone dialer with tel: URI
function normalizePhone(p){
  return String(p || '').replace(/[^\d+]/g, '')
}
function callPhone(e){
  e?.stopPropagation?.();
  const p = normalizePhone(contact.value?.phone)
  if (p) {
    window.location.href = `tel:${p}`
  }
}

// Open default mail client with mailto: URI
function sendEmail(e){
  e?.stopPropagation?.();
  const email = (contact.value?.email || '').trim()
  if (email) {
    window.location.href = `mailto:${email}`
  }
}
</script>

<template>

  <!-- FEATURED layout -->
  <GeneralEmbedButton
    v-if="layout==='featured'"
    class="contact-details-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onClick"
  >
    <!-- Header: icon + centered title + share -->
    <div class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
      <!-- Left Contact Icon -->
      <div class="d-flex align-items-center">
        <!-- Inline SVG contact icon with configurable color -->
        <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Contact details icon">
          <path :fill="appearance.button_font_color || '#0f172a'" d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z"/>
        </svg>
      </div>
      <!-- Center Title -->
      <CardTitle :title="link.title || 'Contact details'" layout="classic" align="center" :max="100" class="flex-grow-1" style="min-width:0" />
      <!-- Share icon on the right -->
      <span class="d-flex align-items-center" aria-label="Share" style="flex-shrink:0">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="SVGRepo_iconCarrier">
            <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                  d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
          </g>
        </svg>
      </span>
    </div>

    <!-- Simple visual area: show banner if provided; otherwise show phone/email with icons -->
    <div class="text-center mb-2">
      <!-- Banner image when file exists -->
      <div
        v-if="link?.file"
        class="mx-auto"
        :style="{
          width: '100%',
          height: '160px',
          borderRadius: '12px 12px 0 0',
          backgroundImage: `url(${coverImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }"
      ></div>

      <!-- Contact details fallback when no file -->
      <div
        v-else
        :class="appearance.buttons"
        class="mx-auto d-flex flex-column justify-content-center align-items-start"
        :style="{
          width: '100%',
          minHeight: '120px',
          padding: '12px 16px',
          gap: '10px'
        }"
      >
        <!-- Phone row -->
        <div v-if="contact?.phone" class="d-flex align-items-center" style="gap:10px; cursor:pointer;" @click.stop.prevent="callPhone" >
          <!-- Phone icon -->
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path :fill="appearance.button_font_color || '#0f172a'" fill-rule="evenodd" clip-rule="evenodd"
                  d="M3.654 1.328A2.25 2.25 0 0 1 6.5 1.2l2.036.815c.74.296 1.237 1.01 1.237 1.81v2.17a2.25 2.25 0 0 1-.77 1.69l-1.06.927c.409 1.155 1.163 2.36 2.264 3.461 1.101 1.102 2.306 1.855 3.461 2.264l.927-1.06a2.25 2.25 0 0 1 1.69-.77h2.17c.8 0 1.514.497 1.81 1.237l.816 2.036a2.25 2.25 0 0 1-.129 2.846l-1.44 1.727c-.497.595-1.264.872-2.012.72-3.2-.654-6.377-2.54-9.304-5.468-2.927-2.927-4.814-6.104-5.468-9.304-.152-.748.125-1.515.72-2.012l1.727-1.44Z" />
          </svg>
          <div class="d-flex flex-column text-start" style="min-width:0;">
            <span class="fw-semibold" :style="{ color: appearance.button_font_color || '#0f172a' }">{{ contact?.phone_type || 'Phone' }}</span>
            <span class="text-truncate" :style="{ color: appearance.button_font_color || '#0f172a', opacity: 0.85 }">{{ contact?.phone }}</span>
          </div>
        </div>

        <!-- Email row -->
        <div v-if="contact?.email" class="d-flex align-items-center" style="gap:10px; cursor:pointer;" @click.stop.prevent="sendEmail">
          <!-- Email icon -->
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path :fill="appearance.button_font_color || '#0f172a'" fill-rule="evenodd" clip-rule="evenodd"
                  d="M2 5.75A2.75 2.75 0 0 1 4.75 3h14.5A2.75 2.75 0 0 1 22 5.75v12.5A2.75 2.75 0 0 1 19.25 21H4.75A2.75 2.75 0 0 1 2 18.25V5.75Zm3.03.72A1.25 1.25 0 0 0 3.5 7.5v.208l7.56 4.725a1.25 1.25 0 0 0 1.38 0L20 7.708V7.5a1.25 1.25 0 0 0-1.53-1.03L12 9.457 5.03 6.47Z" />
          </svg>
          <div class="d-flex flex-column text-start" style="min-width:0;">
            <span class="fw-semibold" :style="{ color: appearance.button_font_color || '#0f172a' }">{{ contact?.email_type || 'Email' }}</span>
            <span class="text-truncate" :style="{ color: appearance.button_font_color || '#0f172a', opacity: 0.85 }">{{ contact?.email }}</span>
          </div>
        </div>
      </div>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC layout -->
  <GeneralButton
    v-else
    class="contact-details-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    :preventNav="true"
    @click="onClick"
    @share="onShare"
  >
    <!-- Force left icon to contact icon when no file/platform icon -->
    <template #left>
      <div class="classic-icon d-flex align-items-center justify-content-center" :class="appearance.buttons">
        <!-- Inline SVG contact icon with configurable color -->
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Contact details icon">
          <path :fill="appearance.button_font_color || '#0f172a'" d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z"/>
        </svg>
      </div>
    </template>
    <template #title>
      <CardTitle :title="link.title || 'Contact details'" layout="classic" align="start" :max="100" />
    </template>
    <!-- Use default unified share icon from GeneralButton via actions slot omission -->
  </GeneralButton>
</template>

<style scoped>
.contact-details-card :deep(iframe){ border:0; }
</style>
